import { useMotionValue } from 'motion/react';
import { useCallback, useEffect } from 'react';

/**
 * Custom scroll progress hook that extends Motion's (motion.dev / Framer Motion) useScroll functionality.
 *
 * This is needed because Framer Motion's useScroll only updates on actual scroll events,
 * but doesn't recalculate progress when content changes without scrolling (e.g., adding/removing tabs).
 *
 * Returns the same API as useScroll: { scrollXProgress, scrollYProgress }
 */
type UseScrollProgressProps = {
  container: React.RefObject<HTMLElement>;
};

export const useScrollProgress = ({ container }: UseScrollProgressProps) => {
  const scrollXProgress = useMotionValue(0);
  const scrollYProgress = useMotionValue(0);

  const calculateProgress = useCallback(() => {
    const element = container.current;
    if (!element) return { x: 0, y: 0 };

    const scrollLeft = element.scrollLeft;
    const scrollTop = element.scrollTop;
    const scrollWidth = element.scrollWidth;
    const scrollHeight = element.scrollHeight;
    const clientWidth = element.clientWidth;
    const clientHeight = element.clientHeight;

    // Calculate horizontal progress (0 when no horizontal overflow)
    const xProgress = scrollWidth <= clientWidth ? 0 : scrollLeft / (scrollWidth - clientWidth);

    // Calculate vertical progress (0 when no vertical overflow)
    const yProgress = scrollHeight <= clientHeight ? 0 : scrollTop / (scrollHeight - clientHeight);

    return { x: xProgress, y: yProgress };
  }, [container]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;

    const initialProgress = calculateProgress();
    scrollXProgress.set(initialProgress.x);
    scrollYProgress.set(initialProgress.y);

    // Standard scroll event listener - updates during user scrolling
    const handleScroll = () => {
      const progress = calculateProgress();
      scrollXProgress.set(progress.x);
      scrollYProgress.set(progress.y);
    };
    element.addEventListener('scroll', handleScroll);

    /**
     * MutationObserver - KEY FEATURE: Detects DOM changes without scroll events
     * This is the main reason for this custom hook. Motion's useScroll
     * only updates on scroll events, but when content is dynamically added/removed
     * (like tabs in our component), the scrollable area changes but no scroll event
     * occurs, so progress doesn't update. MutationObserver fixes this.
     */
    const mutationObserver = new MutationObserver(() => {
      const progress = calculateProgress();
      scrollXProgress.set(progress.x);
      scrollYProgress.set(progress.y);
    });

    mutationObserver.observe(element, {
      childList: true, // Detect child elements being added/removed
      subtree: true, // Watch all descendants (nested elements)
    });

    // ResizeObserver - Updates progress when container or content resizes
    const resizeObserver = new ResizeObserver(() => {
      const progress = calculateProgress();
      scrollXProgress.set(progress.x);
      scrollYProgress.set(progress.y);
    });
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [calculateProgress, scrollXProgress, scrollYProgress, container]);

  /**
   * Returns the same API as Framer Motion's useScroll:
   * - scrollXProgress: MotionValue<number> (0-1) horizontal scroll progress
   * - scrollYProgress: MotionValue<number> (0-1) vertical scroll progress
   *
   * Both are compatible with:
   * - useMotionValueEvent for change listeners
   * - useTransform for derived values
   * - Motion component style props
   */
  return {
    scrollXProgress,
    scrollYProgress,
  };
};
